import { useEffect } from 'react';
import { BrowserRouter, useRoutes, useLocation, Navigate } from 'react-router-dom';
import Privacy from './pages/Privacy/Privacy';
import Terms from './pages/Terms/Terms';
import Artists440 from './pages/440Artists/440Artists';
import NotFound from './pages/NotFound';
import FormPage from './pages/FormPage/FormPage';
import { formPages } from './pages/FormPage/formPages';
import LegalPage from './pages/LegalPage/LegalPage';
import { legalPages } from './pages/LegalPage/legalPages';
import ThankYouPage from './pages/ThankYouPage/ThankYouPage';
import { thankYouPages } from './pages/ThankYouPage/thankYouPages';
import CancellationCalculator from './pages/CancellationCalculator/CancellationCalculator';
import { useSearchParams } from "react-router-dom";
import { ContentProvider } from './context/ContentContext';
import { redirects } from './redirects';

const Redirect = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
};

const AppRouter = () => {

  return useRoutes([
    {
      path: '/terms', element: (
        <Terms />
      )
    },
    {
      path: '/privacy', element: (
        <Privacy />
      )
    },
    {
      path: '/', element: (
        <Artists440 />
      )
    },
    ...formPages.map((page) => ({ path: page.path, element: <FormPage page={page} /> })),
    ...legalPages.map((page) => ({ path: page.path, element: <LegalPage page={page} /> })),
    ...thankYouPages.map((page) => ({ path: page.path, element: <ThankYouPage page={page} /> })),
    { path: '/cancellation-calculator/', element: <CancellationCalculator /> },
    ...Object.entries(redirects).map(([from, to]) => ({ path: from, element: <Redirect to={to} /> })),
    { path: '*', element: <NotFound /> },
  ]);
};

const HashScrollHandler = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      // Remove the '#' from the hash and strip any query parameters
      const hashWithoutHash = location.hash.substring(1);
      const id = hashWithoutHash.split('?')[0];

      // Small delay to ensure the DOM is ready
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  return null;
};

const GlobalParamsHandler = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleInteraction = (e) => {
      const anchor = e.target.closest("a");
      if (!anchor || !anchor.href || anchor.dataset.utmProcessed) return;

      const originalHref = anchor.getAttribute("href");

      // Skip empty or hash links
      if (!originalHref || originalHref.startsWith("#")) return;

      const queryString = searchParams.toString();
      if (!queryString) return;

      const hasQuery = anchor.href.includes("?");
      const newUrl = hasQuery
        ? `${anchor.href}&${queryString}`
        : `${anchor.href}?${queryString}`;

      anchor.href = newUrl;

      // Avoid re-processing
      anchor.dataset.utmProcessed = "true";
    };

    document.addEventListener("click", handleInteraction, true);
    document.addEventListener("contextmenu", handleInteraction, true);

    return () => {
      document.removeEventListener("click", handleInteraction, true);
      document.removeEventListener("contextmenu", handleInteraction, true);
    };
  }, [searchParams]);

  return null;
};

export default function App() {
  return (
    <BrowserRouter>
      <HashScrollHandler />
      <GlobalParamsHandler />
      <ContentProvider>
        <AppRouter />
      </ContentProvider>
    </BrowserRouter>
  )
};
